import React, { useState, useEffect } from 'react';
import { Check, Trash2, Plus } from 'lucide-react';
import { GradedPart } from '../../services/auto-coring/AutoCoringService';
import KaTeXRenderer from '../common/KaTeXRenderer';

interface ResultEditorProps {
    parts: GradedPart[];
    onConfirm: (updatedParts: GradedPart[], options: { penalizeMissingUnit: boolean }) => void;
}

const ResultEditor: React.FC<ResultEditorProps> = ({ parts, onConfirm }) => {
    const [editedParts, setEditedParts] = useState<GradedPart[]>(parts);
    const [penalizeMissingUnit, setPenalizeMissingUnit] = useState(false);

    useEffect(() => {
        setEditedParts(parts);
    }, [parts]);

    const handleChange = (index: number, newContent: string) => {
        const newParts = [...editedParts];
        newParts[index] = { ...newParts[index], content: newContent };
        setEditedParts(newParts);
    };

    const handleDelete = (index: number) => {
        const newParts = editedParts.filter((_, i) => i !== index);
        setEditedParts(newParts);
    };

    const handleAddPart = () => {
        setEditedParts([...editedParts, { content: '' }]);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-bold text-gray-800">Kiểm tra nội dung nhận diện</h3>
                <div className="flex flex-col gap-1">
                    <p className="text-sm text-gray-500">Chỉnh sửa nội dung bên trái, xem kết quả hiển thị bên phải.</p>
                    <p className="text-xs text-blue-600">💡 Mẹo: Dùng dấu $ bao quanh công thức toán. Ví dụ: $E = mc^2$</p>
                </div>
            </div>

            <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
                {editedParts.map((part, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-gray-500 uppercase">Ý số {index + 1}</span>
                            <button
                                onClick={() => handleDelete(index)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                title="Xóa dòng này"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Input Area */}
                            <div className="flex flex-col">
                                <textarea
                                    value={part.content}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    className="w-full h-full min-h-[80px] p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 font-mono text-sm resize-y"
                                    placeholder="Nhập công thức hoặc nội dung..."
                                />
                            </div>

                            {/* Preview Area */}
                            <div className="bg-blue-50/50 rounded-lg border border-blue-100 p-3 min-h-[80px] flex items-center">
                                <div className="w-full text-gray-800">
                                    {part.content ? (
                                        <KaTeXRenderer content={part.content} />
                                    ) : (
                                        <span className="text-gray-400 italic text-sm">Preview hiển thị tại đây...</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {editedParts.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                        Chưa có nội dung nào. Nhấn "Thêm dòng" để bắt đầu.
                    </div>
                )}

                <div className="p-4 bg-gray-50/50">
                    <button
                        onClick={handleAddPart}
                        className="flex items-center gap-2 text-blue-600 font-medium hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors w-full justify-center border border-dashed border-blue-200"
                    >
                        <Plus size={18} />
                        Thêm dòng mới
                    </button>
                </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={penalizeMissingUnit}
                        onChange={(e) => setPenalizeMissingUnit(e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700 font-medium">Trừ 0.25đ nếu thiếu đơn vị</span>
                </label>

                <button
                    onClick={() => onConfirm(editedParts, { penalizeMissingUnit })}
                    className="btn-primary px-6 py-2 rounded-lg flex items-center gap-2"
                >
                    <Check size={20} />
                    Xong
                </button>
            </div>
        </div>
    );
};

export default ResultEditor;
